import React from 'react';
import { TextField as MuiTextField } from '@mui/material';
import PropTypes from 'prop-types';

/**
 * TextField Atom
 * A base text field component that applies our design system styles
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - Input label
 * @param {string} props.placeholder - Input placeholder
 * @param {string} props.helperText - Helper text
 * @param {string} props.error - Error message
 * @param {string} props.value - Input value
 * @param {Function} props.onChange - Change handler
 * @param {string} props.type - Input type (text, password, etc.)
 * @param {boolean} props.fullWidth - Whether field should take full width
 * @param {boolean} props.disabled - Whether field is disabled
 * @param {boolean} props.required - Whether field is required
 * @param {string} props.size - Field size ('small', 'medium', 'singleLine', 'multiLine', 'commentField')
 * @param {number} props.rows - Number of rows for multiline input
 * @param {boolean} props.multiline - Whether field is multiline
 * @param {React.ReactNode} props.startAdornment - Adornment at the start
 * @param {React.ReactNode} props.endAdornment - Adornment at the end
 * @param {Object} props.InputProps - Props for the Input component
 * @param {Object} props.sx - Additional styles
 * @returns {React.ReactElement} TextField component
 */
const TextField = ({
  label,
  placeholder,
  helperText,
  error,
  value,
  onChange,
  type = 'text',
  fullWidth = true,
  disabled = false,
  required = false,
  size = 'medium',
  rows,
  multiline = false,
  startAdornment,
  endAdornment,
  InputProps = {},
  sx = {},
  ...rest
}) => {
  // Determine className based on size for custom field sizes
  let className = '';
  if (size === 'singleLine') className = 'single-line-field';
  if (size === 'multiLine') className = 'multi-line-field';
  if (size === 'commentField') className = 'comment-field';
  
  // Handle adornments if provided
  const finalInputProps = {
    ...InputProps,
  };
  
  if (startAdornment) {
    finalInputProps.startAdornment = startAdornment;
  }
  
  if (endAdornment) {
    finalInputProps.endAdornment = endAdornment;
  }

  return (
    <MuiTextField
      label={label}
      placeholder={placeholder}
      helperText={helperText}
      error={!!error}
      value={value}
      onChange={onChange}
      type={type}
      fullWidth={fullWidth}
      disabled={disabled}
      required={required}
      size={['small', 'medium'].includes(size) ? size : 'medium'}
      rows={rows}
      multiline={multiline}
      className={className}
      InputProps={finalInputProps}
      sx={sx}
      {...rest}
    />
  );
};

TextField.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
  error: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  type: PropTypes.string,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large', 'singleLine', 'multiLine', 'commentField']),
  rows: PropTypes.number,
  multiline: PropTypes.bool,
  startAdornment: PropTypes.node,
  endAdornment: PropTypes.node,
  InputProps: PropTypes.object,
  sx: PropTypes.object
};

export default TextField;